/**
 * Hub ask actions shared by the Inbox list, the decision view (legacy questions) and bulk
 * actions: answer a question, resolve an approval, land, snooze. Private asks are claimed on the
 * hub first, so one answer goes out per ask however many taps or devices.
 */
import { useQueryClient } from '@tanstack/react-query'
import { type ApprovalDecision, isFullSha } from '@viko/protocol'
import { ApiError } from '../../lib/api.js'
import { CommandRejected, useBackend } from '../../lib/backend.js'
import { actionErrorText } from '../../lib/inbox-actions.js'
import { landGrantOpt, useLandGrantPicker } from '../../lib/land-grant.js' // land-grant
import { answerOnce } from '../../lib/private-inbox.js'
import { qk } from '../../lib/queries.js'
import type { InboxItem } from '../../lib/types.js'
import { errorText, useToast } from '../ui.js'

const answerIds = new Map<string, string>()

export function useAskActions() {
  const b = useBackend()
  const grantFor = useLandGrantPicker() // land-grant
  const qc = useQueryClient()
  const toast = useToast()

  const resolveLocal = (ids: string[]) =>
    qc.setQueryData<InboxItem[]>(qk.inbox, (prev) => prev?.filter((i) => !ids.includes(i.id)))

  const deliver = async <K extends 'run.answer' | 'run.approve'>(
    item: InboxItem,
    kind: K,
    payload: Parameters<typeof b.dispatch<K>>[2],
  ): Promise<boolean> => {
    if (!item.machineId) return false
    const machineId = item.machineId
    if (!item.private) {
      await b.dispatch(kind, machineId, payload)
      return true
    }
    const r = await answerOnce(
      {
        claim: (id, commandId) => b.api.answerClaim(id, commandId),
        send: (commandId) => b.dispatch(kind, machineId, payload, { id: commandId }),
        isDuplicate: (e) =>
          (e instanceof ApiError && e.code === 'id_conflict') ||
          (e instanceof CommandRejected && e.outcome.status === 'duplicate'),
      },
      answerIds,
      item.itemId ?? item.id,
    )()
    if (r === 'already-answered') toast('Already answered on another device')
    return true
  }

  return {
    resolveLocal,
    /** `run.answer` for a hub question (CLI or legacy `viko_ask` without an entity). */
    async answer(item: InboxItem, answers: Record<string, string[]>, quiet = false) {
      try {
        if (
          !(await deliver(item, 'run.answer', { runId: item.runId, questionId: item.id, answers }))
        )
          return false
        resolveLocal([item.id])
        if (!quiet) toast('Answer sent')
        return true
      } catch (e) {
        toast(errorText(e), 'error')
        return false
      }
    },
    async decide(item: InboxItem, decision: ApprovalDecision, message?: string) {
      try {
        if (
          !(await deliver(item, 'run.approve', {
            runId: item.runId,
            approvalId: item.id,
            decision,
            ...(message ? { message } : {}),
          }))
        )
          return false
        resolveLocal([item.id])
        toast(decision === 'deny' ? 'Denied' : 'Approved')
        return true
      } catch (e) {
        toast(errorText(e), 'error')
        return false
      }
    },
    async land(item: InboxItem) {
      const machineId = item.machineId ?? (item.body as { machineId?: string } | null)?.machineId
      if (!machineId || !item.land) {
        toast('This landing has no machine yet.', 'error')
        return false
      }
      try {
        await b.dispatch(
          'land.merge',
          machineId,
          {
            workstreamId: item.land.workstreamId,
            ...(item.runId ? { runId: item.runId } : {}),
            ...(item.land.branch ? { branch: item.land.branch } : {}),
            // g18-stack: the approval covers exactly the HEAD this card shows.
            ...(isFullSha(item.land.headSha) ? { headSha: item.land.headSha } : {}),
            target: 'main',
          },
          {
            summary: `Land ${item.land.branch || 'this branch'} into main`,
            // land-grant: within a standing grant, no passkey tap.
            ...landGrantOpt(grantFor({ workstreamId: item.land.workstreamId }, machineId)),
          },
        )
        resolveLocal([item.id])
        toast('Landing started')
        return true
      } catch (e) {
        toast(errorText(e), 'error')
        return false
      }
    },
    /** Hub snooze (the hub hides the item for every device until then). */
    async snooze(item: InboxItem, until: number) {
      try {
        await b.api.snooze(item.itemId ?? item.id, until)
        resolveLocal([item.id])
        void qc.invalidateQueries({ queryKey: qk.ranked })
        return true
      } catch (e) {
        toast(actionErrorText(e), 'error')
        return false
      }
    },
  }
}
